import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError, cachedGet } from "@/lib/api";
import {
  Wallet, Users, BookOpenCheck, Receipt, BellRing, AlertTriangle,
  Heart, Calendar, Sparkles, ArrowRight,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import SetupChecklistCard from "@/components/SetupChecklistCard";

export default function ClientDashboard() {
  const { user } = useAuth();
  const [budget, setBudget] = useState(null);
  const [insights, setInsights] = useState(null);
  const [pending, setPending] = useState([]);
  const [workers, setWorkers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        // Stale-while-revalidate each tile independently — the user sees the
        // previous numbers instantly and they refresh in place.
        await Promise.all([
          cachedGet("/dashboard/budget", null, (data) => setBudget(data)),
          cachedGet("/dashboard/financial-insights", null, (data) => setInsights(data)),
          cachedGet("/notifications", { status: "pending" }, (data) => setPending(data)),
          cachedGet("/workers", null, (data) => setWorkers(data)),
        ]);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const usedPct = budget?.pct_used || 0;
  const projWarn = budget?.projection?.projected_overspend > 0;

  return (
    <div className="space-y-6 max-w-7xl" data-testid="client-dashboard">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl overflow-hidden ring-4 ring-client/25 bg-muted shrink-0 grid place-items-center font-display font-black text-2xl text-client" data-testid="dashboard-avatar">
            {user?.avatar_url
              ? <img src={user.avatar_url} alt="me" className="w-full h-full object-cover" />
              : (user?.full_name || "?")[0]}
          </div>
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground font-bold">Welcome back</p>
            <h1 className="text-4xl sm:text-5xl font-display font-black tracking-tight" data-testid="dashboard-greeting">
              Hello, {user?.nickname || user?.full_name?.split(" ")[0]} <span className="inline-block animate-float">👋</span>
            </h1>
            <p className="text-muted-foreground mt-1">Here's your care snapshot. <Link to="/profile" className="text-client underline font-bold" data-testid="edit-profile-link">Edit profile</Link></p>
          </div>
        </div>
        <Link to="/notifications">
          <Button className="rounded-full min-h-[48px] gap-2 font-bold" data-testid="header-approvals-btn"
                  style={{ background: pending.length ? "hsl(var(--coral))" : "hsl(var(--client))", color: "white" }}>
            <BellRing className="w-4 h-4" />
            {pending.length} pending approval{pending.length !== 1 && "s"}
          </Button>
        </Link>
      </header>

      <SetupChecklistCard />

      {/* NDIS Budget hero */}
      <Card className="rounded-3xl p-6 sm:p-8 border-2 card-lift relative overflow-hidden" data-testid="budget-hero-card">
        <div className="blob animate-blob" style={{ width: 280, height: 280, top: -100, right: -100, background: "hsl(var(--client) / 0.2)" }} />
        <div className="relative z-10 grid lg:grid-cols-[1.4fr,1fr] gap-8">
          <div>
            <Badge className="rounded-full bg-client/10 text-client border-2 border-client/20 px-3 py-1 font-bold" data-testid="budget-badge">
              <Wallet className="w-3.5 h-3.5 mr-1.5" /> NDIS PLAN UTILISATION
            </Badge>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-display font-black text-5xl sm:text-6xl tracking-tight" data-testid="budget-spent-amount">
                ${(budget?.spent || 0).toLocaleString()}
              </span>
              <span className="text-muted-foreground text-lg">/ ${(budget?.total_budget || 0).toLocaleString()}</span>
            </div>
            <Progress value={Math.min(usedPct, 100)} className="h-4 rounded-full mt-4" data-testid="budget-progress" />
            <div className="mt-3 flex flex-wrap gap-3 text-sm">
              <Stat label="Used" value={`${usedPct}%`} />
              <Stat label="Remaining" value={`$${(budget?.remaining || 0).toLocaleString()}`} accent="success" />
              <Stat label="Pending payments" value={`$${(budget?.pending || 0).toLocaleString()}`} accent="coral" />
            </div>
            {budget?.projection && (
              <div className={`mt-4 rounded-2xl p-4 text-sm border-2 ${projWarn ? "border-destructive/30 bg-destructive/5" : "border-client/20 bg-client/5"}`} data-testid="budget-projection">
                <div className="font-bold mb-1">Projection</div>
                Day {budget.projection.days_elapsed} of {budget.projection.total_days} —
                projected end-of-plan spend <b>${budget.projection.projected_end_spend.toLocaleString()}</b>
                {projWarn && <span className="text-destructive font-bold"> (over budget by ${budget.projection.projected_overspend.toLocaleString()})</span>}
              </div>
            )}
            <Link to="/budget"><Button variant="outline" className="rounded-full mt-4 min-h-[44px] font-bold border-2" data-testid="manage-budget-btn">
              Manage budget <ArrowRight className="w-4 h-4 ml-1" />
            </Button></Link>
          </div>
          <div className="space-y-3">
            <Tile to="/workers" icon={Users} label="Support workers" value={workers.length} color="client" />
            <Tile to="/invoices" icon={Receipt} label="YTD billing" value={`$${(insights?.total_paid + insights?.total_pending || 0).toLocaleString()}`} color="coral" />
            <Tile to="/financial" icon={Sparkles} label="Pending payments" value={`$${(insights?.total_pending || 0).toLocaleString()}`} color="sun" />
          </div>
        </div>
      </Card>

      {/* Quick actions */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="quick-actions">
        {[
          { to: "/timesheets/new", icon: BookOpenCheck, label: "New timesheet", color: "linear-gradient(135deg, hsl(var(--client)), hsl(var(--coral)))" },
          { to: "/invoices", icon: Receipt, label: "Invoices", color: "linear-gradient(135deg, hsl(var(--coral)), hsl(var(--sun)))" },
          { to: "/roster", icon: Calendar, label: "Roster", color: "linear-gradient(135deg, #7B5EA7, #EE82A2)" },
          { to: "/vitals", icon: Heart, label: "Vital signs", color: "linear-gradient(135deg, #48CAE4, #1E6091)" },
          { to: "/incidents", icon: AlertTriangle, label: "Log incident", color: "linear-gradient(135deg, #F46036, #D72660)" },
          { to: "/kilometres", icon: Wallet, label: "Kilometres", color: "linear-gradient(135deg, #5A8A4A, #C97B53)" },
        ].map((a) => (
          <Link key={a.to} to={a.to}
            data-testid={`quick-${a.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="rounded-2xl p-4 card-lift flex flex-col gap-2 text-white shadow-md"
            style={{ background: a.color }}>
            <a.icon className="w-6 h-6" strokeWidth={2.5} />
            <span className="font-bold text-sm leading-tight">{a.label}</span>
          </Link>
        ))}
      </div>

      {/* Pending approvals strip */}
      {pending.length > 0 && (
        <Card className="rounded-3xl p-5 border-2 border-coral/30 bg-coral/5" data-testid="pending-approvals-card">
          <div className="flex items-center justify-between">
            <div>
              <Badge className="bg-coral text-white rounded-full font-bold px-3 py-1">ACTION NEEDED</Badge>
              <h3 className="font-display font-extrabold text-xl mt-2">{pending.length} pending approval{pending.length !== 1 && "s"}</h3>
              <p className="text-sm text-muted-foreground">Your support workers are waiting for sign-off.</p>
            </div>
            <Link to="/notifications"><Button className="rounded-full font-bold min-h-[48px]" style={{ background: "hsl(var(--coral))" }} data-testid="review-approvals-btn">Review now</Button></Link>
          </div>
        </Card>
      )}

      {/* Per-worker insights */}
      <Card className="rounded-3xl p-6 border-2" data-testid="per-worker-insights">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-extrabold text-2xl">Earnings by worker (YTD)</h3>
          <Link to="/financial"><Button variant="ghost" size="sm" className="font-bold rounded-full" data-testid="view-financial-link">View details <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
        </div>
        {!insights?.per_worker?.length ? (
          <p className="text-sm text-muted-foreground">No earnings yet. <Link to="/timesheets/new" className="text-client underline">Log a timesheet</Link>.</p>
        ) : (
          <div className="space-y-3">
            {insights.per_worker.map((p) => {
              const max = Math.max(...insights.per_worker.map((x) => x.ytd), 1);
              const w = workers.find((x) => x.id === p.worker_id);
              return (
                <div key={p.worker_id} className="flex items-center gap-3" data-testid={`worker-row-${p.worker_id}`}>
                  <div className="w-10 h-10 rounded-full ring-2 ring-coral/30 overflow-hidden bg-muted shrink-0 grid place-items-center font-bold text-coral text-sm">
                    {w?.photo_url ? <img src={w.photo_url} alt={p.worker_name} className="w-full h-full object-cover" /> : (p.worker_name?.[0] || "?")}
                  </div>
                  <div className="w-1/3 sm:w-1/4 font-semibold text-sm truncate">{p.worker_name}</div>
                  <div className="flex-1 h-3 rounded-full bg-muted overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(p.ytd / max) * 100}%`, background: "linear-gradient(90deg, hsl(var(--client)), hsl(var(--coral)))" }} />
                  </div>
                  <div className="w-24 text-right font-mono text-sm font-bold">${p.ytd.toLocaleString()}</div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-full px-3 py-1.5 bg-muted/60 border" data-testid={`stat-${label.toLowerCase()}`}>
      <span className="text-xs text-muted-foreground mr-1.5 font-semibold uppercase tracking-wider">{label}</span>
      <span className={`font-bold ${accent === "coral" ? "text-coral" : accent === "success" ? "text-emerald-700" : ""}`}>{value}</span>
    </div>
  );
}

function Tile({ to, icon: Icon, label, value, color }) {
  return (
    <Link to={to} className="rounded-2xl p-4 border-2 bg-card card-lift block" data-testid={`tile-${label.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: `hsl(var(--${color}) / 0.15)` }}>
          <Icon className="w-5 h-5" style={{ color: `hsl(var(--${color}))` }} strokeWidth={2.6} />
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
          <div className="font-display font-black text-xl">{value}</div>
        </div>
      </div>
    </Link>
  );
}
